import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MapPin, Phone, Wheelchair, Hospital, Airplane, CheckCircle,
  ArrowsCounterClockwise, NavigationArrow, SignOut, TrafficCone, Truck, Person,
} from "@phosphor-icons/react";
import { apiClient } from "../lib/api";
import { toast } from "sonner";

const STEPS = [
  { key: "accepted", label: "Accept" },
  { key: "en_route", label: "On the way" },
  { key: "arrived",  label: "Arrived" },
  { key: "loaded",   label: "Rider loaded" },
  { key: "dropped",  label: "Dropped off" },
];

export default function DriverPortal() {
  const { pin } = useParams();
  const navigate = useNavigate();
  const [driver, setDriver] = useState(null);
  const [trips, setTrips] = useState([]);
  const [error, setError] = useState(null);
  const [pingingId, setPingingId] = useState(null);
  const watchRef = useRef(null);
  const pingTimerRef = useRef(null);

  useEffect(() => {
    // Try login with PIN from URL
    apiClient.post("/driver/login", { pin })
      .then(({ data }) => setDriver(data))
      .catch((e) => setError(e?.response?.data?.detail || "Invalid PIN"));
  }, [pin]);

  useEffect(() => {
    if (!driver) return;
    let alive = true;
    const load = async () => {
      try {
        const { data } = await apiClient.get(`/driver/${pin}/trips`);
        if (alive) setTrips(data);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Failed to load trips");
      }
    };
    load();
    const t = setInterval(load, 15000);
    return () => { alive = false; clearInterval(t); };
  }, [driver, pin]);

  const updateStatus = async (bookingId, status) => {
    // POD gating — block "Dropped" if no POD captured yet
    if (status === "dropped") {
      const trip = trips.find((t) => t.id === bookingId);
      if (!trip?.pod_photo_public_url) {
        toast.error("Capture a POD photo + signature before marking dropped.");
        return;
      }
    }
    try {
      // Capture current location if available
      let coords = null;
      if (navigator.geolocation) {
        coords = await new Promise((resolve) =>
          navigator.geolocation.getCurrentPosition(
            (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
            () => resolve(null),
            { enableHighAccuracy: true, timeout: 5000 }
          )
        );
      }
      const body = { status, ...(coords || {}) };
      await apiClient.post(`/driver/${pin}/trips/${bookingId}/status`, body);
      toast.success(`Marked ${status.replace("_", " ")}`);
      // Refresh
      const { data } = await apiClient.get(`/driver/${pin}/trips`);
      setTrips(data);
      // Start or stop GPS pinger
      if (["accepted", "en_route", "arrived", "loaded"].includes(status)) startPing(bookingId);
      if (["dropped", "cancelled"].includes(status)) stopPing();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const startPing = (bookingId) => {
    stopPing();
    if (!navigator.geolocation) return;
    setPingingId(bookingId);
    watchRef.current = navigator.geolocation.watchPosition(
      () => {},
      () => {},
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 8000 }
    );
    pingTimerRef.current = setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        async (p) => {
          try {
            await apiClient.post(`/driver/${pin}/trips/${bookingId}/ping`,
              { lat: p.coords.latitude, lng: p.coords.longitude });
          } catch (_) { /* ignore transient */ }
        },
        () => {},
        { enableHighAccuracy: true, timeout: 8000 }
      );
    }, 15000);
  };
  const stopPing = () => {
    if (watchRef.current && navigator.geolocation?.clearWatch) navigator.geolocation.clearWatch(watchRef.current);
    watchRef.current = null;
    if (pingTimerRef.current) clearInterval(pingTimerRef.current);
    pingTimerRef.current = null;
    setPingingId(null);
  };
  useEffect(() => () => stopPing(), []);

  if (error) {
    return (
      <main className="min-h-screen bg-brandblue-ink text-white grid place-items-center px-6" data-testid="driver-portal-error">
        <div className="text-center max-w-sm">
          <img src="/logo.png" alt="" className="h-14 w-14 mx-auto logo-glow" />
          <h1 className="mt-6 font-heading text-3xl font-black">Driver portal</h1>
          <p className="mt-3 text-white/60 text-sm">{error}</p>
          <button onClick={() => navigate("/")} className="mt-6 inline-flex items-center gap-2 text-brandblue hover:text-white text-sm">
            Back to site
          </button>
        </div>
      </main>
    );
  }
  if (!driver) {
    return <main className="min-h-screen bg-brandblue-ink text-white grid place-items-center text-sm text-white/60">Loading…</main>;
  }

  const openNav = (lat, lng) => {
    if (!lat || !lng) return;
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`, "_blank");
  };

  const tripIcon = (t) => {
    if (t.trip_type === "airport") return <Airplane size={16} weight="fill" className="text-brandblue" />;
    if (t.mobility_type === "wheelchair") return <Wheelchair size={16} weight="fill" className="text-brandblue" />;
    return <Hospital size={16} weight="fill" className="text-brandblue" />;
  };

  return (
    <main className="min-h-screen bg-brandblue-ink text-white" data-testid="driver-portal">
      <div aria-hidden className="orb h-[380px] w-[380px] bg-brandblue/25 -top-24 -left-24" />
      <header className="relative z-10 max-w-md mx-auto px-5 pt-6 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="relative inline-flex h-10 w-10 items-center justify-center rounded-md bg-transparent overflow-hidden">
            <img src="/logo.png" alt="" className="h-9 w-9" />
          </span>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-brandblue font-bold">Driver portal</div>
            <div className="font-heading text-base font-black leading-none mt-0.5">{driver.name}</div>
          </div>
        </div>
        <button onClick={() => { stopPing(); navigate("/"); }} className="h-9 w-9 grid place-items-center rounded-md bg-white/[0.06] border border-white/10 hover:bg-white/10" data-testid="driver-logout">
          <SignOut size={14} />
        </button>
      </header>

      <section className="relative z-10 max-w-md mx-auto px-5 pb-24 space-y-4">
        <div className="rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur p-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-white/60">My queue</div>
            <div className="font-heading text-2xl font-black leading-none mt-1">{trips.length} trip{trips.length !== 1 ? "s" : ""}</div>
          </div>
          <div className="text-xs text-white/60 inline-flex items-center gap-1.5">
            <span className={`h-2 w-2 rounded-full ${pingingId ? "bg-emerald-400 live-dot" : "bg-white/30"}`} />
            {pingingId ? "GPS live" : "idle"}
          </div>
        </div>

        {trips.length === 0 && (
          <div className="rounded-xl border border-dashed border-white/15 bg-white/[0.02] p-6 text-center text-white/50 text-sm">
            No assigned trips yet. A dispatcher will push one to you shortly.
          </div>
        )}

        {trips.map((t) => {
          const currentDriverStep = STEPS.findIndex((s) => s.key === t.driver_trip_status);
          return (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.05] to-white/[0.02] backdrop-blur p-4"
              data-testid={`driver-trip-card-${t.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold">
                  {tripIcon(t)}
                  <span className="text-white/80">{t.trip_type.replace(/_/g, " ")}</span>
                  {t.flight_number && <span className="text-brandblue font-mono">· {t.flight_number}</span>}
                </div>
                <span className="text-[10px] font-mono text-white/40" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>
                  #{t.id.slice(0, 6)}
                </span>
              </div>
              <div className="mt-3 font-heading text-xl font-black leading-tight">
                {t.pickup_date} · {t.pickup_time}
              </div>
              <div className="mt-2 text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold">Rider</div>
              <div className="text-sm text-white inline-flex items-center gap-2">
                <Person size={14} /> {t.patient_initials} · <Wheelchair size={12} className="text-brandblue" /> {t.mobility_type}
              </div>

              <div className="mt-3 space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <span className="mt-1 h-2 w-2 rounded-full bg-forest shrink-0" />
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-white/50">Pickup</div>
                    <div className="text-white leading-snug">{t.pickup_address}</div>
                  </div>
                  <button onClick={() => openNav(t.pickup_lat, t.pickup_lng)} className="h-8 px-2 rounded-md bg-white/[0.06] border border-white/10 hover:bg-white/10 text-xs inline-flex items-center gap-1">
                    <NavigationArrow size={12} weight="fill" /> Nav
                  </button>
                </div>
                <div className="flex items-start gap-2">
                  <span className="mt-1 h-2 w-2 rounded-full bg-terracotta shrink-0" />
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-white/50">Drop-off</div>
                    <div className="text-white leading-snug">{t.dropoff_address}</div>
                  </div>
                  <button onClick={() => openNav(t.dropoff_lat, t.dropoff_lng)} className="h-8 px-2 rounded-md bg-white/[0.06] border border-white/10 hover:bg-white/10 text-xs inline-flex items-center gap-1">
                    <NavigationArrow size={12} weight="fill" /> Nav
                  </button>
                </div>
              </div>

              {t.notes && (
                <div className="mt-3 rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2 text-xs text-white/80 inline-flex items-start gap-2">
                  <TrafficCone size={12} className="mt-0.5 text-brandblue" />
                  <span>{t.notes}</span>
                </div>
              )}

              {/* step buttons */}
              <div className="mt-4 grid grid-cols-5 gap-1.5">
                {STEPS.map((s, i) => {
                  const done = i <= currentDriverStep;
                  const next = i === Math.max(0, currentDriverStep + 1) || (currentDriverStep === -1 && i === 0);
                  const isDropped = s.key === "dropped";
                  const podMissing = isDropped && !t.pod_photo_public_url;
                  return (
                    <button
                      key={s.key}
                      onClick={() => updateStatus(t.id, s.key)}
                      disabled={i < currentDriverStep || (isDropped && podMissing)}
                      data-testid={`driver-step-${s.key}-${t.id}`}
                      title={isDropped && podMissing ? "Capture POD photo first" : ""}
                      className={`px-1 py-2.5 rounded-md text-[10px] font-semibold uppercase tracking-wider transition ${
                        done
                          ? "bg-brandblue text-white"
                          : next && !podMissing
                            ? "bg-white/10 hover:bg-white/20 text-white border border-brandblue/50"
                            : "bg-white/[0.04] text-white/40"
                      }`}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>

              {/* POD capture block — visible after rider is loaded */}
              {currentDriverStep >= 3 && (
                <PodCaptureBlock
                  trip={t}
                  pin={pin}
                  onCaptured={async () => {
                    const { data } = await apiClient.get(`/driver/${pin}/trips`);
                    setTrips(data);
                  }}
                />
              )}

              <div className="mt-3 flex items-center justify-between text-[10px] text-white/40">
                <span className="inline-flex items-center gap-1">
                  <Truck size={10} /> {t.facility_name}
                </span>
                {t.driver_trip_status && (
                  <span className="inline-flex items-center gap-1 text-brandblue">
                    <CheckCircle size={10} weight="fill" /> {t.driver_trip_status.replace(/_/g, " ")}
                  </span>
                )}
              </div>
            </motion.div>
          );
        })}
      </section>
    </main>
  );
}

function PodCaptureBlock({ trip, pin, onCaptured }) {
  const [preview, setPreview] = useState(null);
  const [signature, setSignature] = useState("");
  const [uploading, setUploading] = useState(false);
  const alreadyUploaded = !!trip.pod_photo_public_url;

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6_000_000) {
      toast.error("POD photo too large — try a smaller picture.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const upload = async () => {
    if (!preview) {
      toast.error("Take a POD photo first.");
      return;
    }
    setUploading(true);
    try {
      await apiClient.post(`/driver/${pin}/trips/${trip.id}/pod`, {
        image_base64: preview,
        signature_text: signature || null,
      });
      toast.success("POD captured ✓");
      setPreview(null);
      setSignature("");
      onCaptured?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "POD upload failed");
    } finally {
      setUploading(false);
    }
  };

  if (alreadyUploaded) {
    return (
      <div className="mt-3 rounded-lg bg-emerald-500/10 border border-emerald-400/30 px-3 py-2 text-emerald-200 text-xs inline-flex items-center gap-2" data-testid={`pod-done-${trip.id}`}>
        <CheckCircle weight="fill" size={12} /> POD captured · ready to mark dropped
        {trip.pod_signature_text && <span className="opacity-70">· "{trip.pod_signature_text}"</span>}
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-xl border-2 border-dashed border-brandblue/50 bg-brandblue/5 p-3 space-y-2" data-testid={`pod-block-${trip.id}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-brandblue">
        Capture proof of delivery (required for Medicaid)
      </div>
      <input
        type="file"
        accept="image/*"
        capture="environment"
        onChange={onFile}
        data-testid={`pod-file-${trip.id}`}
        className="block text-xs text-white/80 file:mr-3 file:rounded-md file:border-0 file:bg-brandblue file:text-white file:px-3 file:py-1.5 file:font-semibold file:text-xs"
      />
      {preview && (
        <img
          src={preview}
          alt="POD preview"
          className="rounded-md border border-white/15 max-h-40 object-cover"
          data-testid={`pod-preview-${trip.id}`}
        />
      )}
      <input
        type="text"
        value={signature}
        onChange={(e) => setSignature(e.target.value)}
        placeholder="Typed signature of patient / representative"
        maxLength={120}
        data-testid={`pod-signature-${trip.id}`}
        className="w-full rounded-md bg-white/[0.04] border border-white/15 px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-brandblue"
      />
      <button
        type="button"
        onClick={upload}
        disabled={!preview || uploading}
        data-testid={`pod-upload-${trip.id}`}
        className="w-full rounded-md bg-brandblue hover:bg-brandblue-hover disabled:opacity-60 text-white py-2 text-xs font-bold uppercase tracking-wider transition"
      >
        {uploading ? "Uploading…" : "Save POD"}
      </button>
    </div>
  );
}
